import { ErrorHandler, InfoTooltip, TableTransfer } from '@common/components';
import React, { useMemo, useState } from 'react';
import { filterableColumnBuilder } from 'works/legacy/lib/table-filters-helper';
import { SupplierType } from '@institutions/types';
import { SupplierStatus } from '@institutions/components';
import { Select } from 'antd';
import { SupplierStatusTypes } from '@common/graphql';
import { ApolloError, gql, useQuery } from '@common/hooks';

type Props = {
  selectedSupplierIds: string[];
  onSelectSupplierIdsChange: (selectedIds: string[]) => void;
  suppliers: SupplierType[];
  loading: boolean;
  error: ApolloError | undefined;
};

const transferTitles = {
  leftSingular: 'Supplier Available',
  leftPlural: 'Suppliers Available',
  rightSingular: 'Supplier Selected',
  rightPlural: 'Suppliers Selected',
};

type Columns = {
  name: string;
  statusForCurrentOrganization: {
    status: SupplierStatusTypes;
  };
};

function generateRowKey(data: SupplierType[]) {
  return data.map((supplier) => ({
    key: supplier.id,
    selectKey: supplier.id,
    ...supplier,
  }));
}

const column = filterableColumnBuilder<Columns>({});

const tableColumns = [
  column({
    key: 'name',
    dataIndex: 'name',
    title: 'Supplier Name',
  }),
  column({
    key: 'statusForCurrentOrganization',
    dataIndex: ['statusForCurrentOrganization', 'status'],
    title: 'Status',
    render: (status) => <SupplierStatus status={status} />,
  }),
];

const UnitBulkSupplierPermissionsOrgBussinessQuery = gql`
  query UnitBulkSupplierPermissions {
    organizationBusinesses {
      id
      name
      suppliers {
        id
      }
    }
  }
`;

type UnitBulkSupplierPermissionsOrgBussinessResponse = {
  organizationBusinesses: {
    id: string;
    name: string;
    suppliers: {
      id: string;
    }[];
  }[];
};

export function SuppliersManagementBulkLocationsUpdateStepSuppliers({
  selectedSupplierIds,
  onSelectSupplierIdsChange,
  suppliers,
  loading,
  error,
}: Props) {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const {
    data: organizationBusinessesData,
    loading: organizationBusinessesLoading,
    error: organizationBusinessesError,
  } = useQuery<UnitBulkSupplierPermissionsOrgBussinessResponse>(
    UnitBulkSupplierPermissionsOrgBussinessQuery
  );

  const flattenedBusinesses = useMemo(() => {
    return organizationBusinessesData?.organizationBusinesses.reduce(
      (acc, business) => {
        const suppliersIds = business.suppliers.map((s) => s.id);
        return [
          ...acc,
          { id: business.id, name: business.name, suppliersIds: suppliersIds },
        ];
      },
      [] as { id: string; name: string; suppliersIds: string[] }[]
    );
  }, [organizationBusinessesData]);

  const anyError = error || organizationBusinessesError;
  if (anyError) {
    return (
      <ErrorHandler
        error={anyError}
        log="SuppliersManagementBulkLocationsUpdateStepSuppliers"
      />
    );
  }

  const onChange = (targetKeys: string[]) => {
    onSelectSupplierIdsChange(targetKeys);
  };

  const onSelectChange = (
    sourceSelectedKeys: string[],
    targetSelectedKeys: string[]
  ) => {
    const newSelectedKeys = [...sourceSelectedKeys, ...targetSelectedKeys];

    setSelectedKeys(newSelectedKeys);
  };

  return (
    <>
      <div>
        <span>Use the supplier&apos;s allowance list as:</span>
        <InfoTooltip
          size="md"
          leftOffset="lg"
          title="Select here if you’d like to preselect a supplier’s locations list from another location."
        />
      </div>
      <Select
        showSearch
        size="large"
        style={{ width: 'calc(50% - 24px)' }}
        loading={organizationBusinessesLoading}
        onSelect={(businessId) => {
          selectedSupplierIds = [];
          setSelectedKeys([]);
          const suppliersIds = flattenedBusinesses?.find(
            (b) => b.id === businessId
          )?.suppliersIds;
          if (!suppliersIds) {
            return;
          }
          const newSelectedKeys = [...selectedKeys, ...suppliersIds];
          const uniqueSelectedKeys = newSelectedKeys.filter(
            (v, i, a) => a.indexOf(v) === i
          );
          setSelectedKeys(uniqueSelectedKeys);
        }}
      >
        {flattenedBusinesses?.map((business) => (
          <Select.Option key={business.id} value={business.id}>
            {business.name}
          </Select.Option>
        ))}
      </Select>
      <TableTransfer
        showSearch
        selectKeyEnabled
        titles={transferTitles}
        loading={loading}
        dataSource={generateRowKey(suppliers)}
        selectedKeys={selectedKeys}
        onSelectChange={onSelectChange}
        targetKeys={selectedSupplierIds}
        columns={tableColumns}
        onChange={onChange}
        className="suppliers-management-bulk-locations-update-step-suppliers"
      />
      <style jsx>{`
        :global(.suppliers-management-bulk-locations-update-step-suppliers) {
          margin-top: 24px;
        }
      `}</style>
    </>
  );
}
